import { buildLayoutFieldTypeIndex } from "@/lib/layoutRenderer/utils/layout-field-type-index.util";
import type {
	DynamicFormSchema,
	FormField,
} from "../interfaces/dynamicForm.interface";
import { persistedDefinitionToFormField } from "./persisted-field-definition.util";
import { isCheckedValue } from "./is-checked-value.util";
import { storedFileId } from "./stored-file-id.util";
import { storedStringList } from "./stored-string-list.util";

export interface RequiredFieldRuntimeState {
	shouldShow?: boolean;
	isRequired?: boolean;
}

/**
 * Whether a required field's current value counts as filled.
 * Mirrors the required branch of {@link createFieldValidator}.
 */
export function hasRequiredFieldValue(
	field: FormField,
	value: unknown,
): boolean {
	switch (field.type) {
		case "checkbox":
		case "switch":
			return isCheckedValue(value);
		case "toggleButton":
			if (field.exclusive) {
				return typeof value === "string" && value.length > 0;
			}
			return Array.isArray(value) && value.length > 0;
		case "select":
		case "autocomplete":
			if (field.multiple) {
				return Array.isArray(value) && value.length > 0;
			}
			return value != null && value !== "";
		case "file":
			return storedFileId(value) != null;
		case "multiSelect":
		case "users":
			return storedStringList(value).length > 0;
		case "address":
			return typeof value === "string" && value.trim().length > 0;
		case "signature":
			return typeof value === "string" && value.trim().length > 0;
		case "number":
		case "slider":
			return value !== null && value !== undefined && value !== "";
		case "group":
		case "fieldArray":
			return true;
		default:
			return value != null && value !== "";
	}
}

/**
 * Returns true when every **visible required** field has a value.
 *
 * Used to enable create-modal Add — not full Zod (optional empties / format rules
 * must not block the button; those run on submit via {@link generateZodSchema}).
 */
export function areRequiredDynamicFormFieldsSatisfied(
	schema: DynamicFormSchema,
	values: Record<string, unknown>,
	fieldStates?: Record<string, RequiredFieldRuntimeState>,
): boolean {
	const layoutFieldTypeByDefinitionKey = buildLayoutFieldTypeIndex(
		schema.layoutJson,
	);

	for (const def of schema.fieldDefinitions) {
		const field = persistedDefinitionToFormField(def, {
			layoutFieldTypeByDefinitionKey,
		});
		if (!field) continue;

		const state = fieldStates?.[field.id];
		const shouldShow = state?.shouldShow ?? !field.hidden;
		if (shouldShow === false) continue;

		const isRequired = state?.isRequired ?? field.required ?? false;
		if (!isRequired) continue;

		if (!hasRequiredFieldValue(field, values[field.name])) {
			return false;
		}
	}

	return true;
}
